/** Consulta de guías cargadas: buscador, filtros y detalle. */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Search, Ban, Pencil, FileSpreadsheet } from 'lucide-react';
import {
  Boton,
  Campo,
  Chip,
  EstadoGuiaChip,
  Selector,
  Tarjeta,
  TarjetaTitulo,
  Vacio,
} from '@/components/ui';
import { COL } from '@/db/firebase';
import { actualizar } from '@/db/repositorio';
import type { DocGuia } from '@/db/tiposDatos';
import { useCatalogos } from '@/hooks/useCatalogos';
import { useDialogo } from '@/components/ui/dialogo';
import { fecha as ffecha, m3 as fm3 } from '@/domain/formato';
import { formatearMinutos } from '@/domain/tiempos';
import { aFechaISO } from '@/domain/formato';
import { useGuias } from '@/hooks/useGuias';

function inicioDeMes(): string {
  const d = new Date();
  return aFechaISO(new Date(d.getFullYear(), d.getMonth(), 1));
}

export default function ListaGuias() {
  const { catalogos } = useCatalogos();
  const { pedirTexto } = useDialogo();
  const { guias, cargando } = useGuias();

  const [busqueda, setBusqueda] = useState('');
  const [desde, setDesde] = useState(inicioDeMes());
  const [hasta, setHasta] = useState(aFechaISO(new Date()));
  const [planta, setPlanta] = useState('');
  const [estado, setEstado] = useState('');

  /** La guía más reciente que hay cargada, sin importar los filtros. */
  const ultimaFecha = guias[0]?.fecha ?? null;

  /** Deja los filtros de fecha sobre el mes de esa guía. */
  function irAlUltimoMes() {
    if (!ultimaFecha) return;
    const [a, m] = ultimaFecha.split('-').map(Number);
    const fin = new Date(a!, m!, 0);
    setDesde(`${a}-${String(m).padStart(2, '0')}-01`);
    setHasta(aFechaISO(fin));
  }

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return guias.filter((g) => {
      if (desde && g.fecha < desde) return false;
      if (hasta && g.fecha > hasta) return false;
      if (planta && g.plantaCodigo !== planta) return false;
      if (estado && g.estado !== estado) return false;
      if (!q) return true;
      return [
        String(g.folio),
        String(g.remito ?? ''),
        g.clienteNombre,
        g.productoCodigo,
        g.destinoTexto,
        g.centroCostoCodigo,
        g.equipoCodigo,
        g.operadorMixerNombre,
        g.solicitante,
        g.observaciones,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [guias, busqueda, desde, hasta, planta, estado]);

  const totales = useMemo(
    () => ({
      viajes: filtradas.length,
      m3: filtradas.reduce((s, g) => s + (g.cantidadM3 || 0), 0),
      bloques: filtradas.reduce((s, g) => s + (g.bloquesCodelco ?? 0), 0),
      incompletas: filtradas.filter((g) => !g.tiemposCompletos).length,
    }),
    [filtradas],
  );

  async function anular(g: DocGuia) {
    const motivo = await pedirTexto({
      titulo: `Anular la guía ${g.folio}`,
      mensaje:
        'La guía queda registrada como anulada y deja de sumar al estado de pago, ' +
        'pero conserva su folio para el control del talonario.',
      etiqueta: 'Motivo de la anulación',
      marcador: 'Ej.: camión devuelto sin descargar',
      aceptar: 'Anular guía',
      tono: 'peligro',
    });
    if (!motivo) return;
    await actualizar(COL.guias, g.id, { estado: 'ANULADA', motivoAnulacion: motivo }, null);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Guías de despacho</h1>
          <p className="text-xs text-texto-mute">
            {totales.viajes} viajes · {fm3(totales.m3)} m³ · {totales.bloques} bloques de
            sobrestadía
            {totales.incompletas > 0 && (
              <span className="text-fluor-amarillo"> · {totales.incompletas} sin horarios</span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Boton
            variante="secundario"
            icono={<FileSpreadsheet size={15} />}
            onClick={() =>
              void import('@/db/exportarExcel').then((m) => m.exportarLibroGuias(filtradas))
            }
            disabled={!filtradas.length}
          >
            Exportar a Excel
          </Boton>
          <Link to="/guias/nueva">
            <Boton variante="primario">Nueva guía</Boton>
          </Link>
        </div>
      </div>

      <Tarjeta>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-5">
          <Campo
            etiqueta="Buscar"
            placeholder="Folio, cliente, producto, destino…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="lg:col-span-2"
            sufijo={<Search size={14} />}
          />
          <Campo
            etiqueta="Desde"
            type="date"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
          />
          <Campo
            etiqueta="Hasta"
            type="date"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-2">
            <Selector etiqueta="Planta" value={planta} onChange={(e) => setPlanta(e.target.value)}>
              <option value="">Todas</option>
              {catalogos.plantas.map((p) => (
                <option key={p.id} value={p.codigoPlanta}>
                  {p.codigoPlanta}
                </option>
              ))}
            </Selector>
            <Selector etiqueta="Estado" value={estado} onChange={(e) => setEstado(e.target.value)}>
              <option value="">Todos</option>
              <option value="BORRADOR">Borrador</option>
              <option value="EMITIDA">Emitida</option>
              <option value="ANULADA">Anulada</option>
              <option value="REQUIERE_CLASIFICACION">Por clasificar</option>
            </Selector>
          </div>
        </div>
      </Tarjeta>

      <Tarjeta>
        <TarjetaTitulo>Resultados</TarjetaTitulo>

        {cargando ? (
          <Vacio titulo="Cargando…" />
        ) : filtradas.length === 0 ? (
          <Vacio icono={<FileText size={30} />} titulo="Sin guías en este rango">
            {guias.length > 0 && ultimaFecha ? (
              // Con el libro histórico recién importado, el rango por defecto
              // (este mes) queda vacío aunque haya miles de guías cargadas.
              <span className="flex flex-col items-center gap-2">
                <span>
                  Hay {guias.length.toLocaleString('es-CL')} guías cargadas, pero ninguna entre
                  estas fechas. La más reciente es del {ffecha(ultimaFecha)}.
                </span>
                <Boton variante="secundario" tamano="sm" onClick={irAlUltimoMes}>
                  Ver ese mes
                </Boton>
              </span>
            ) : (
              'Ajusta los filtros o carga la primera guía del período.'
            )}
          </Vacio>
        ) : (
          <>
            {/* Escritorio */}
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-linea text-left text-[11px] uppercase tracking-wide text-texto-mute">
                    <th className="px-4 py-2 font-medium">Fecha</th>
                    <th className="px-3 py-2 font-medium">Folio</th>
                    <th className="px-3 py-2 font-medium">Planta</th>
                    <th className="px-3 py-2 font-medium">Cliente</th>
                    <th className="px-3 py-2 font-medium">Producto</th>
                    <th className="px-3 py-2 text-right font-medium">m³</th>
                    <th className="px-3 py-2 font-medium">Destino</th>
                    <th className="px-3 py-2 text-right font-medium">Estadía</th>
                    <th className="px-3 py-2 text-right font-medium">Bloques</th>
                    <th className="px-3 py-2 font-medium">Estado</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {filtradas.map((g) => (
                    <tr key={g.id} className="border-b border-linea/60 hover:bg-white/[.03]">
                      <td className="whitespace-nowrap px-4 py-2 text-xs">{ffecha(g.fecha)}</td>
                      <td className="px-3 py-2 font-mono text-xs tabular-nums">{g.folio}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-xs">{g.plantaCodigo}</td>
                      <td className="max-w-[10rem] truncate px-3 py-2 text-xs">
                        {g.clienteNombre}
                      </td>
                      <td className="max-w-[11rem] truncate px-3 py-2 font-mono text-xs">
                        {g.productoCodigo}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">
                        {fm3(g.cantidadM3)}
                      </td>
                      <td className="max-w-[12rem] truncate px-3 py-2 text-xs">{g.destinoTexto}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">
                        {formatearMinutos(g.minEstadiaObra)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs tabular-nums">
                        {g.bloquesCodelco ? (
                          <span className="text-fluor-amarillo">{g.bloquesCodelco}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <EstadoGuiaChip estado={g.estado} />
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right">
                        <Link to={`/guias/${g.id}`}>
                          <Boton variante="fantasma" tamano="sm" icono={<Pencil size={13} />} />
                        </Link>
                        {g.estado !== 'ANULADA' && (
                          <Boton
                            variante="fantasma"
                            tamano="sm"
                            icono={<Ban size={13} />}
                            onClick={() => void anular(g)}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Móvil */}
            <div className="divide-y divide-linea/60 md:hidden">
              {filtradas.map((g) => (
                <Link
                  key={g.id}
                  to={`/guias/${g.id}`}
                  className="block px-4 py-3 active:bg-white/5"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-sm font-semibold">#{g.folio}</span>
                    <EstadoGuiaChip estado={g.estado} />
                  </div>
                  <div className="mt-1 text-xs text-texto-mute">
                    {ffecha(g.fecha)} · {g.plantaCodigo} · turno {g.turno}
                  </div>
                  <div className="mt-1.5 text-sm">{g.clienteNombre}</div>
                  <div className="font-mono text-xs text-texto-mute">{g.productoCodigo}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Chip>{fm3(g.cantidadM3)} m³</Chip>
                    {g.bloquesCodelco ? (
                      <Chip nivel="AMARILLO">{g.bloquesCodelco} bloques</Chip>
                    ) : null}
                    {!g.tiemposCompletos && <Chip nivel="AMARILLO">Sin horarios</Chip>}
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </Tarjeta>
    </div>
  );
}
